import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  MdClose,
  MdPhone,
  MdEmail,
  MdOutlineInfo,
  MdSecurity,
  MdBlock,
} from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { TbShieldAlert, TbClockHour4 } from "react-icons/tb";
import { useLockBodyScroll } from "../../Hooks/useLockBodyScroll";

const PenaltyInfoModal = ({ isOpen, onClose }) => {
  const [animate, setAnimate] = useState(false);
  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 250);
  };

  if (!isOpen) return null;

  const handleCallSupport = () => {
    window.open("tel:+918339903307", "_self");
  };

  const handleEmailSupport = () => {
    const subject = encodeURIComponent("Supplier Penalty Payment Inquiry");
    const body = encodeURIComponent(
      "Hello Support Team,\n\nI have inquiries regarding my supplier penalties and would like assistance with settlement.\n\nThank you."
    );
    window.open(
      `mailto:pcmexportsbbsr@gmail.com?subject=${subject}&body=${body}`,
      "_self"
    );
  };

  const handleWhatsAppSupport = () => {
    const message = encodeURIComponent(
      "Hello, I have inquiries regarding pending penalties on PCM Exports. Please assist me. Thank you."
    );
    window.open(`https://wa.me/918339903307?text=${message}`, "_blank");
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className={`bg-white dark:bg-gray-900 w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden ${
          animate
            ? "scale-100 translate-y-0"
            : "scale-95 md:translate-y-4 translate-y-full"
        }`}
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center justify-between flex-shrink-0">
          <button
            onClick={handleClose}
            className="text-2xl leading-none text-white/90 hover:text-white transition cursor-pointer"
          >
            ←
          </button>
          <div className="flex-1 text-center font-semibold text-lg text-white">
            About Supplier Penalties
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/15 transition cursor-pointer"
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
          {/* Section 1: Overview */}
          <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 space-y-1.5">
            <h4 className="font-bold text-gray-900 dark:text-white text-xs flex items-center gap-1.5">
              <TbClockHour4 className="text-primaryColor" size={16} />
              What are Supplier Penalties?
            </h4>
            <p className="text-gray-500 dark:text-gray-400 text-[11px]">
              Penalties are levied when an order return or quality dispute is
              resolved in favor of the buyer, or when non-fulfillment conditions
              occur in accordance with platform merchant policies.
            </p>
          </div>

          {/* Section 2: Account Blocking warning */}
          <div className="p-3.5 rounded-xl bg-red-50 text-red-800 border border-red-200/80 space-y-1.5">
            <h4 className="font-bold text-red-900 text-xs flex items-center gap-1.5">
              <MdBlock className="text-red-600" size={16} />
              Overdue & Blocking Rules
            </h4>
            <p className="text-[11px] text-red-700 leading-normal">
              Each penalty has a specified due date. If penalties remain unpaid
              past their due date, automated scheduler restrictions activate,
              which block live quotation submissions and app access until settled.
            </p>
          </div>

          {/* Section 3: Instant Clearance via Razorpay */}
          <div className="p-3.5 rounded-xl bg-emerald-50 text-emerald-900 border border-emerald-200/80 space-y-1.5">
            <h4 className="font-bold text-emerald-900 text-xs flex items-center gap-1.5">
              <MdSecurity className="text-emerald-600" size={16} />
              Instant Razorpay Clearance
            </h4>
            <p className="text-[11px] text-emerald-700 leading-normal">
              You can pay your penalties directly using Razorpay (UPI, Debit/Credit
              Cards, Net Banking). Upon successful transaction, penalties are marked
              Paid instantly and any blocking flags on your account are cleared
              automatically.
            </p>
          </div>

          {/* Section 4: Need Help? Contact support */}
          <div className="pt-2">
            <h4 className="font-bold text-gray-900 dark:text-white text-xs uppercase tracking-wider mb-2">
              Need Assistance or Offline Settlement?
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={handleCallSupport}
                className="flex items-center justify-center gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primaryColor hover:bg-gray-50 dark:hover:bg-gray-800 transition text-gray-700 dark:text-gray-200 font-semibold cursor-pointer"
              >
                <MdPhone className="text-primaryColor" size={16} />
                <span>Call Us</span>
              </button>

              <button
                type="button"
                onClick={handleWhatsAppSupport}
                className="flex items-center justify-center gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-emerald-400 hover:bg-emerald-50/50 transition text-gray-700 dark:text-gray-200 font-semibold cursor-pointer"
              >
                <FaWhatsapp className="text-emerald-500" size={16} />
                <span>WhatsApp</span>
              </button>

              <button
                type="button"
                onClick={handleEmailSupport}
                className="flex items-center justify-center gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:bg-blue-50/50 transition text-gray-700 dark:text-gray-200 font-semibold cursor-pointer"
              >
                <MdEmail className="text-blue-500" size={16} />
                <span>Email</span>
              </button>
            </div>
            <p className="text-[10px] text-gray-400 text-center mt-2">
              Support lines active Mon–Sat, 9:00 AM – 7:00 PM IST
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2 rounded-xl bg-primaryColor text-white font-bold text-xs hover:bg-primaryColor/90 transition cursor-pointer shadow-xs"
          >
            Got It
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PenaltyInfoModal;
